import React from 'react';
import { List, ListSubheader } from '@material-ui/core';
import { DatadogOnCallEmptyState } from './DatadogOnCallEmptyState';
import { DatadogOnCallUser } from './DatadogOnCallUser';
import { DatadogOnCallEntry } from './types';
import useAsync from 'react-use/lib/useAsync';
import { Alert } from '@material-ui/lab';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';

type Props = {
  name: string;
  teamName: string;
};

type ScheduleData = {
  id: string;
  attributes: {
    name: string;
    time_zone: string;
  };
};

type OnCallResponse = {
  data?: {
    id: string;
    attributes?: {
      start?: string;
      end?: string;
    };
  };
  included?: Array<{
    id: string;
    type: string;
    attributes?: {
      name?: string;
      email?: string;
    };
  }>;
};

export const DatadogOnCallSchedule = ({ name, teamName }: Props) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const { value: entries, loading, error } = useAsync(async () => {
    const proxyUrl = await discoveryApi.getBaseUrl('proxy');

    const schedulesResponse = await fetchApi.fetch(
      `${proxyUrl}/datadog/api/v2/on-call/schedules`,
    );

    if (!schedulesResponse.ok) {
      throw new Error(
        `Failed to fetch schedules: ${schedulesResponse.statusText}`,
      );
    }

    const schedulesData = await schedulesResponse.json();

    console.log('[DatadogOnCall] All schedules from API:', schedulesData.data?.map((s: ScheduleData) => s.attributes.name));

    const matchingSchedules: ScheduleData[] = (schedulesData.data || []).filter(
      (schedule: ScheduleData) => schedule.attributes.name.startsWith(teamName),
    );

    console.log('[DatadogOnCall] Matching schedules for team:', teamName, matchingSchedules.map(s => s.attributes.name));

    if (matchingSchedules.length === 0) {
      console.log('[DatadogOnCall] No matching schedules found');
      return [];
    }

    const onCallEntries: DatadogOnCallEntry[] = [];

    for (const schedule of matchingSchedules) {
      console.log('[DatadogOnCall] Fetching on-call for schedule:', schedule.attributes.name);

      const onCallResponse = await fetchApi.fetch(
        `${proxyUrl}/datadog/api/v2/on-call/schedules/${schedule.id}/on-call?include=user`,
      );

      if (onCallResponse.ok) {
        const onCallData: OnCallResponse = await onCallResponse.json();

        console.log('[DatadogOnCall] Response for', schedule.attributes.name, ':', {
          hasData: !!onCallData.data,
          includedCount: onCallData.included?.length || 0,
          included: onCallData.included,
        });

        if (onCallData.data && onCallData.included?.length) {
          const user = onCallData.included.find(inc => inc.type === 'users');

          if (user) {
            console.log('[DatadogOnCall] Found user for', schedule.attributes.name, ':', user.attributes?.name);
            onCallEntries.push({
              user: {
                id: user.id,
                type: 'user',
                attributes: {
                  name: user.attributes?.name || 'Unknown',
                  email: user.attributes?.email || '',
                },
              },
              schedule: {
                id: schedule.id,
                type: 'schedule',
                attributes: {
                  name: schedule.attributes.name,
                  time_zone: schedule.attributes.time_zone,
                },
              },
              start: onCallData.data.attributes?.start || '',
              end: onCallData.data.attributes?.end || '',
            });
          } else {
            console.log('[DatadogOnCall] No user found in included for', schedule.attributes.name);
          }
        } else {
          console.log('[DatadogOnCall] No data or included users for', schedule.attributes.name);
        }
      } else {
        console.log('[DatadogOnCall] Failed to fetch on-call for', schedule.attributes.name, ':', onCallResponse.status);
      }
    }

    // Sort entries so "primary" schedules appear first
    return onCallEntries.sort((a, b) => {
      const aName = a.schedule.attributes.name.toLowerCase();
      const bName = b.schedule.attributes.name.toLowerCase();
      const aIsPrimary = aName.includes('primary');
      const bIsPrimary = bName.includes('primary');

      if (aIsPrimary && !bIsPrimary) return -1;
      if (!aIsPrimary && bIsPrimary) return 1;
      return aName.localeCompare(bName);
    });
  }, [teamName]);

  if (error) {
    return (
      <Alert severity="error">
        Error fetching Datadog on-call data: {error.message}
      </Alert>
    );
  }

  if (loading) {
    return <Progress />;
  }

  if (!entries?.length) {
    return (
      <List dense subheader={<ListSubheader>{name}</ListSubheader>}>
        <DatadogOnCallEmptyState />
      </List>
    );
  }

  return (
    <List dense subheader={<ListSubheader>{name}</ListSubheader>}>
      {entries.map((entry, index) => (
        <DatadogOnCallUser key={index} entry={entry} />
      ))}
    </List>
  );
};
